import * as React from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format, startOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Helper to get fiscal year dates (April to March)
const getFiscalYearDates = (year) => {
  // Fiscal year starts April 1 of 'year' and ends March 31 of 'year+1'
  return {
    from: new Date(year, 3, 1), // April 1
    to: new Date(year + 1, 2, 31) // March 31 next year
  };
};

const getCurrentFiscalYear = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  // If before April (months 0-2), fiscal year started last calendar year
  const fiscalYearStart = currentMonth < 3 ? today.getFullYear() - 1 : today.getFullYear();
  return getFiscalYearDates(fiscalYearStart);
};

const getLastFiscalYear = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const fiscalYearStart = currentMonth < 3 ? today.getFullYear() - 2 : today.getFullYear() - 1;
  return getFiscalYearDates(fiscalYearStart);
};

export const DateRangePicker = ({ dateRange, onDateRangeChange, className }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClear = (e) => {
    e.stopPropagation();
    onDateRangeChange({ from: undefined, to: undefined });
  };

  const handlePresetClick = (preset) => {
    const today = startOfDay(new Date());
    let range;

    switch (preset) {
      case 'today':
        range = { from: today, to: today };
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        range = { from: yesterday, to: yesterday };
        break;
      case 'lastWeek':
        const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        range = { from: lastWeekStart, to: lastWeekEnd };
        break;
      case 'thisMonth':
        range = { from: startOfMonth(today), to: endOfMonth(today) };
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        range = { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
        break;
      case 'currentFiscalYear':
        range = getCurrentFiscalYear();
        break;
      case 'lastFiscalYear':
        range = getLastFiscalYear();
        break;
      default:
        return;
    }

    onDateRangeChange(range);
    setIsOpen(false);
  };

  // Format preset date examples
  const today = new Date();
  const yesterday = subDays(today, 1);
  const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
  const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
  const thisMonthStart = startOfMonth(today);
  const thisMonthEnd = endOfMonth(today);
  const lastMonth = subMonths(today, 1);
  const currentFY = getCurrentFiscalYear();
  const lastFY = getLastFiscalYear();

  const presets = [
    { 
      key: 'today', 
      label: 'Today', 
      example: format(today, 'dd-MM-yyyy')
    },
    { 
      key: 'yesterday', 
      label: 'Yesterday', 
      example: format(yesterday, 'dd-MM-yyyy')
    },
    { 
      key: 'lastWeek', 
      label: 'Last Week', 
      example: `${format(lastWeekStart, 'dd-MM')} to ${format(lastWeekEnd, 'dd-MM-yyyy')}`
    },
    { 
      key: 'thisMonth', 
      label: 'This Month', 
      example: format(today, 'MMMM yyyy')
    },
    { 
      key: 'lastMonth', 
      label: 'Last Month', 
      example: format(lastMonth, 'MMMM yyyy')
    },
    { 
      key: 'currentFiscalYear', 
      label: 'Current FY', 
      example: `${format(currentFY.from, 'MMM yy')} - ${format(currentFY.to, 'MMM yy')}`
    },
    { 
      key: 'lastFiscalYear', 
      label: 'Last FY', 
      example: `${format(lastFY.from, 'MMM yy')} - ${format(lastFY.to, 'MMM yy')}`
    },
  ];

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="date-range-picker"
            id="date"
            variant="outline"
            className={cn(
              'justify-start text-left font-normal rounded-sm',
              !dateRange?.from && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, 'LLL dd, y')} - {format(dateRange.to, 'LLL dd, y')}
                  <X
                    className="ml-2 h-4 w-4 hover:text-destructive"
                    onClick={handleClear}
                    data-testid="clear-date-range"
                  />
                </>
              ) : (
                format(dateRange.from, 'LLL dd, y')
              )
            ) : (
              <span className="uppercase text-xs tracking-wide">Select Date Range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-sm" align="start">
          <div className="flex">
            {/* Presets sidebar */}
            <div className="border-r border-border p-2 space-y-1 min-w-[160px]">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1">
                Quick Select
              </p>
              {presets.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent transition-colors"
                  data-testid={`preset-${preset.key}`}
                >
                  <div className="font-medium">{preset.label}</div>
                  <div className="text-xs text-muted-foreground">{preset.example}</div>
                </button>
              ))}
            </div>
            {/* Calendar */}
            <div>
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={(range) => {
                  onDateRangeChange(range);
                  if (range?.from && range?.to) {
                    setIsOpen(false);
                  }
                }}
                numberOfMonths={2}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
